import { queryOptions } from "@tanstack/react-query"
import { AuthError, fetchAuthSession } from "aws-amplify/auth"
import {
  GatedSavedPaymentMethodsFailReason,
  GatedSetupIntentWalletFailReason,
  ReserveMembershipsFailReason,
} from "ipctg-inflow-types/InFlowEnums"
import { MembershipMini } from "ipctg-inflow-types/InFlowDB"

import api from "@/utilities/api"

import {
  GatedAccountError,
  GatedSavedPaymentMethodsError,
  GatedSetupIntentWalletError,
  MembershipsError,
} from "@/utilities/expectedErrors"

import {
  ME_BASE_KEY,
  MEMBERSHIPS_BASE_KEY,
  SAVED_PAYMENT_METHODS_BASE_KEY,
  SAVED_PAYMENT_METHODS_WALLET_INTENT,
} from "./keys"

export const meQueryOptions = () => {
  return queryOptions({
    queryKey: [ME_BASE_KEY],
    queryFn: async () => {
      try {
        const session = await fetchAuthSession()
        const cognitoIDToken = session.tokens?.idToken
        if (cognitoIDToken) {
          const [cognitoUser, inflowUser] = await Promise.all([
            api.auth.fetchUserAttributes(),
            api.account.POST({ cognitoIDToken: cognitoIDToken.toString() }),
          ])

          if ("failReason" in inflowUser) {
            throw new GatedAccountError(inflowUser.failReason)
          }

          return { cognitoUser, inflowUser }
        }
        return null
      } catch (e: unknown) {
        if (e instanceof AuthError) {
          if (e.name === "UserUnAuthenticatedException") {
            return null
          }
          if (e.name === "NotAuthorizedException") {
            await api.auth.signOut()
            return null
          }
        }

        console.error(e)
        throw e
      }
    },
    retry: false,
  })
}

export const savedPaymentMethodsOptions = () => {
  return queryOptions({
    queryKey: [ME_BASE_KEY, SAVED_PAYMENT_METHODS_BASE_KEY],
    queryFn: async () => {
      const session = await fetchAuthSession()
      const cognitoIDToken = session.tokens?.idToken?.toString()

      if (!cognitoIDToken)
        throw new GatedSavedPaymentMethodsError(
          GatedSavedPaymentMethodsFailReason.Unauthorized
        )

      const rsp = await api.savedPaymentMethods.POST({ cognitoIDToken })
      if ("failReason" in rsp) {
        if (
          rsp.failReason ===
          GatedSavedPaymentMethodsFailReason.NoSavedPaymentMethods
        )
          return { paymentMethods: [] }
        throw new GatedSavedPaymentMethodsError(rsp.failReason)
      }
      return rsp
    },
  })
}

export const membershipQueryOptions = (args: { locationID: string | null }) => {
  return queryOptions({
    queryKey: [ME_BASE_KEY, MEMBERSHIPS_BASE_KEY, args.locationID],
    queryFn: async (): Promise<MembershipMini[]> => {
      const session = await fetchAuthSession()
      const cognitoIDToken = session.tokens?.idToken?.toString()
      if (!cognitoIDToken)
        throw new MembershipsError(
          ReserveMembershipsFailReason.Unauthorized
        )

      const rsp = await api.memberships.POST({
        cognitoIDToken,
        locationID: args.locationID,
      })
      if ("failReason" in rsp) throw new MembershipsError(rsp.failReason)
      return rsp.memberships
    },
    retry: false,
  })
}

export const walletSetupIntentOptions = () => {
  return queryOptions({
    queryKey: [
      ME_BASE_KEY,
      SAVED_PAYMENT_METHODS_BASE_KEY,
      SAVED_PAYMENT_METHODS_WALLET_INTENT,
    ],
    //prevents uneeded refetrches and fixes the double loading bug of stripe forms
    staleTime: Infinity,
    queryFn: async () => {
      const { cognitoIDToken } = await api.auth.fetchAuthSession()
      if (!cognitoIDToken) {
        throw new GatedSetupIntentWalletError(
          GatedSetupIntentWalletFailReason.Unauthorized
        )
      }
      const rsp = await api.setupIntentWallet.POST({
        cognitoIDToken,
      })
      if ("failReason" in rsp) {
        throw new GatedSetupIntentWalletError(rsp.failReason)
      }
      return rsp
    },
  })
}
